import { useRef, useState } from "react";
import {
  getUserDocumentsList,
  postEmployeeDetailsDelete,
  userDocumentAdd,
  userDocumentRemove,
} from "../../api/employeeDetails";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  EyeIcon,
  PencilAltIcon,
  TrashIcon,
  XCircleIcon,
  XIcon,
  PlusIcon,
} from "@heroicons/react/solid";
import Modal from "@mui/material/Modal";
import Modals from "../../components/modal/Modals";
import spinner from "../../assets/images/spinner.gif";
import ZoomImage from "../../components/ZoomImage";
import { downloadUserReport } from "../../api";
import EmployeeTab from "./EmployeeTab";
import useGet from "../../hooks/read/useGet";
import { useQueryClient } from "@tanstack/react-query";
import Pagination from "../../components/pagination";
import Spinner from "../../components/loader/Spinner";
import { Button } from "../../components/ui";
import GeneratePasswordModal from "./GeneratePasswordModal";
import ToolTip from "../../components/ui/ToolTip";
import RoundedContainer from "../../components/containers/RoundedContainer";
import SearchBar from "../../components/inputs/SearchBar";
import toast from "react-hot-toast";
import { InlineLoader } from "../../components/Loader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../../components/table/Table";

function EmployeeDetails() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialEmployeeState = {
    limit: 50,
    search_query: searchParams.get("search_query") || "",
    page_number: searchParams.get("page_number") || 0,
    screen_name: "employee",
  };

  const navigate = useNavigate();
  const eduFileRef = useRef(null);
  const [isConfirmDeleteModal, setIsConfirmDeleteModal] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [isOpenDocumentModal, setIsOpenDocumentModal] = useState(false);
  const [isGeneratePasswordModalOpen, setIsGeneratePasswordModalOpen] =
    useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [documents, setDocuments] = useState([]);

  const handleDelete = () => {
    postEmployeeDetailsDelete(employeeId)
      .then((res) => {
        if (res.status === 200) {
          setIsConfirmDeleteModal(false);
          queryClient.invalidateQueries("employeelist");
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const {
    list: employeeList,
    paramsObject,
    setGetListParams,
  } = useGet({
    url: "employeelist",
    initialData: initialEmployeeState,
  });

  const [removeDocumentConfirmModal, setRemoveDocumentConfirmModal] =
    useState(false);
  const [userId, setUserId] = useState("");
  const [doumentId, setDocumentId] = useState("");
  const [docType, setDocType] = useState("");
  const [isUploadingFile, setIsuploadingFile] = useState(false);

  const handleFetchUserDocuments = (id) => {
    setUserId(id);
    getUserDocumentsList(id)
      .then((res) => {
        if (res.status === 200) {
          setDocuments(res.data.data);
          setIsOpenDocumentModal(true);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleRemoveDocumentConfirmModal = (id) => {
    setDocumentId(id);
    setIsOpenDocumentModal(false);
    setRemoveDocumentConfirmModal(true);
  };

  const handleDeleteDocument = () => {
    userDocumentRemove(doumentId)
      .then((res) => {
        if (res.status === 200) {
          setRemoveDocumentConfirmModal(false);
          setIsOpenDocumentModal(true);
          handleFetchUserDocuments(userId);
        }
        console.log(res);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleAddEduDoc = () => {
    if (!docType || docType === "" || docType === undefined)
      return alert("Please select document type first");
    eduFileRef.current.click();
  };

  const handleFileUpload = (e) => {
    setIsuploadingFile(true);
    let file = e.target.files;
    userDocumentAdd({ userId, file, docType })
      .then((res) => {
        if (res.status === 200) {
          setIsuploadingFile(false);
          setIsOpenDocumentModal(true);
          handleFetchUserDocuments(userId);
        }
      })
      .catch((err) => {
        setIsuploadingFile(false);
        alert("Something went wrong while uploading please try again");
      });
  };

  const handleDownloadReport = () => {
    downloadUserReport()
      .then((res) => {
        // console.log(res)
        // create file link in browser's memory
        const href = URL.createObjectURL(res.data);

        let fileName = `User_Report.xlsx`;
        // create "a" HTLM element with href to file & click
        const link = document.createElement("a");
        link.href = href;
        link.setAttribute("download", fileName); //or any other extension
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => { });
  };

  const handleNavigateToEmployeeDetail = (company_employee_id, username) => {
    navigate(`${company_employee_id}/${username}`);
  };

  const handleOpenGeneratePasswordModal = (id) => {
    setSelectedEmployeeId(id);
    setIsGeneratePasswordModalOpen(true);
  };

  const handleSearch = (e) => {
    let value = e.target.value;
    let data = {
      ...paramsObject,
      search_query: value,
    };
    setGetListParams(data);
    setSearchParams(data);
  };

  const handlePageClick = (val) => {
    let data = {
      ...paramsObject,
      page_number: val.selected,
    };
    setGetListParams(data);
    setSearchParams(data);
  };

  // Add this function to format the date in a simple localized format
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return "Invalid Date";
    }
  };

  return (
    <>
      <div className="pb-10 pt-10">
        <div className=" container">
          <div className=" flex justify-end space-x-2 px-4">
            <Button className={"px-0 py-0"}>
              <Link
                to={"/employee-details/upload"}
                className="px-4 py-2 flex items-center"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Employees
              </Link>
            </Button>
            <Button variant={"ghost"} onClick={handleDownloadReport}>
              <span className=" text-blue-600">Download Report</span>
            </Button>
          </div>
        </div>

        <EmployeeTab />

        <RoundedContainer className={"mx-4"}>
          <div className="w-[400px]">
            <SearchBar
              value={paramsObject.search_query}
              onChange={handleSearch}
            />
          </div>
        </RoundedContainer>
        {employeeList.isLoading ? (
          <Spinner />
        ) : (
          <>
            <RoundedContainer className={"mx-4"}>
              {employeeList.data?.data.length <= 0 ? (
                <div className="text-center">
                  <p className=" text-gray-700 text-3xl mt-4">
                    No Employee Found
                  </p>
                </div>
              ) : (
                <div className="max-w-full overflow-x-auto">
                  <Table className="table-auto w-full rounded-md">
                    <TableHead>
                      <TableHeaderCell>
                        Name
                      </TableHeaderCell>
                      <TableHeaderCell>
                        Email
                      </TableHeaderCell>
                      <TableHeaderCell>
                        Mobile Number
                      </TableHeaderCell>
                      <TableHeaderCell>
                        Employee Code
                      </TableHeaderCell>
                      <TableHeaderCell className="text-center">
                        Documents
                      </TableHeaderCell>
                      <TableHeaderCell className="text-center">
                        Leaves
                      </TableHeaderCell>
                      <TableHeaderCell className="text-center">
                        Password Set?
                      </TableHeaderCell>
                      <TableHeaderCell className="text-center">
                        Modify
                      </TableHeaderCell>
                    </TableHead>
                    <TableBody>
                      {employeeList.data?.data.map((data, index) => {
                        const {
                          employee_name,
                          phone,
                          email,
                          id,
                          type_of_employee,
                          company_employee_id,
                          employee_code,
                        } = data;
                        return (
                          <TableRow
                            key={index}
                            onClick={() =>
                              handleNavigateToEmployeeDetail(
                                company_employee_id,
                                employee_name
                              )
                            }
                            className="cursor-pointer"
                          >
                            <TableCell className="text-dark font-medium text-sm">
                              {employee_name}
                            </TableCell>
                            <TableCell className="text-dark font-medium text-sm">
                              {email}
                            </TableCell>
                            <TableCell className="text-dark font-medium text-sm">
                              {phone}
                            </TableCell>
                            <TableCell className="text-dark font-medium text-sm">
                              {employee_code || "----"}
                            </TableCell>
                            <TableCell className="text-dark font-medium text-sm">
                              <div
                                className="flex items-center justify-center w-full"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Button
                                  onClick={() =>
                                    handleFetchUserDocuments(
                                      company_employee_id
                                    )
                                  }
                                >
                                  <EyeIcon className="w-4 h-4 mr-2" />{" "}
                                  <span className="text-sm">View</span>
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell className="text-dark font-medium text-sm">
                              <div className="flex items-center justify-center w-full">
                                <Link
                                  onClick={(e) => e.stopPropagation()}
                                  to={`/leaves/${company_employee_id}/${employee_name}`}
                                  className="bg-primary text-white rounded-md px-4 py-2 flex items-center space-x-2 justify-center"
                                >
                                  <EyeIcon className="w-4 h-4" />{" "}
                                  <span className="text-sm">View</span>
                                </Link>
                              </div>
                            </TableCell>
                            <TableCell className="text-dark font-medium text-sm">
                              <div className="flex items-center justify-center w-full">
                                {data.password_set ? (
                                  <ToolTip
                                    content={`Last updated at: ${formatDate(
                                      data.last_password_update_on
                                    )}`}
                                  >
                                    <span className="text-green-600">Yes</span>
                                  </ToolTip>
                                ) : (
                                  <div
                                    className="group cursor-pointer"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <span className="text-red-600 group-hover:hidden">
                                      No
                                    </span>
                                    <Button
                                      className="hidden group-hover:block !px-2 text-xs mx-auto"
                                      onClick={() =>
                                        handleOpenGeneratePasswordModal(
                                          company_employee_id
                                        )
                                      }
                                    >
                                      Set Password
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-dark font-medium text-sm">
                              <div className=" flex justify-center space-x-1">
                                <Link
                                  onClick={(e) => e.stopPropagation()}
                                  to={{
                                    // pathname: `/edit-employee-details/${type_of_employee}/${company_employee_id}`,
                                    pathname: `/edit-employee-details/${company_employee_id}`,
                                  }}
                                  className=" "
                                >
                                  <PencilAltIcon className=" text-gray-600 w-6 hover:text-blue-600" />
                                </Link>
                                <div
                                  className=""
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <TrashIcon
                                    className=" text-gray-600 w-6 hover:text-orange-600 cursor-pointer"
                                    onClick={() => {
                                      setIsConfirmDeleteModal(true);
                                      setEmployeeId(company_employee_id);
                                    }}
                                  />
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </RoundedContainer>
            <RoundedContainer className={"mx-4"}>
              <Pagination
                currentPage={+paramsObject.page_number}
                lengthofItems={employeeList.data?.total_count}
                limit={paramsObject.limit}
                onPageChange={handlePageClick}
              />
            </RoundedContainer>
          </>
        )}
      </div>

      {/* Modals */}
      <Modals show={isConfirmDeleteModal}>
        <div>
          <p className=" roboto text-center font-bold text-2xl text-gray-700">
            Are you sure you want to delete?
          </p>
          <div className=" mt-12 space-x-3 text-center ">
            <Button
              className="font-semibold bg-red-200 text-red-700 border-red-800"
              onClick={handleDelete}
            >
              Yes
            </Button>
            <Button
              variant={"outline"}
              className="font-semibold"
              onClick={() => setIsConfirmDeleteModal(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modals>
      <Modal
        open={isOpenDocumentModal}
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
      >
        <div className="w-[60%] bg-white absolute left-1/2 -translate-x-1/2 top-[20%]">
          <div className="document-wrapper px-4 divide-y">
            <div className="document-head py-4">
              <div className="flex items-center justify-between">
                <h3 className=" text-gray-700 text-3xl font-semibold">
                  Documents
                </h3>
                <div
                  className=" w-6 h-6 cursor-pointer"
                  onClick={() => setIsOpenDocumentModal(false)}
                >
                  <XIcon className=" text-black w-full h-full" />
                </div>
              </div>
            </div>
            <div className="document-body py-4 max-h-96 overflow-auto">
              {documents?.length <= 0 ? (
                <div className="text-gray-700 my-5 text-center text-2xl">
                  <h3>No Documents To Show</h3>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-3">
                  {documents?.map((document, idx) => {
                    const {
                      file_name,
                      media_id,
                      media_type,
                      document_media_type,
                    } = document;
                    return (
                      <>
                        {document_media_type === "pdf" ? (
                          <a
                            href={media_type}
                            target="_blank"
                            rel="noopener noreferrer"
                            className=" text-center py-2 px-2 rounded bg-gray-400 text-gray-700"
                          >
                            View PDF
                          </a>
                        ) : (
                          // <ZoomImage key={idx} className='h-full'>
                          //    <img src={media_type} alt="dummy" className='border-2 border-black w-full h-full' />
                          // </ZoomImage>
                          <div
                            key={idx}
                            className="w-full h-full border-2 border-black relative"
                          >
                            <div
                              className="w-6 h-6 absolute cursor-pointer right-2 top-2 z-20"
                              onClick={() =>
                                handleRemoveDocumentConfirmModal(media_id)
                              }
                            >
                              <XCircleIcon className=" w-full h-full" />
                            </div>
                            <ZoomImage>
                              <img
                                src={media_type}
                                alt="documents"
                                className="w-full h-full object-cover"
                              />
                            </ZoomImage>
                          </div>
                        )}
                      </>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="document-foot space-x-4 py-4">
              {isUploadingFile ? (
                <div className=" w-8 h-8 mx-auto">
                  <img src={spinner} alt="spinner" className="w-full h-full" />
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-3">
                  <input
                    ref={eduFileRef}
                    type="file"
                    className="hidden"
                    multiple
                    onChange={handleFileUpload}
                  />
                  <select
                    name="documentType"
                    className="input"
                    onChange={(e) => setDocType(e.target.value)}
                  >
                    <option value="">-- Select Document Type --</option>
                    <option value="EDUCTIONAL">Education</option>
                    <option value="WORK">Work</option>
                  </select>
                  <Button onClick={handleAddEduDoc}>Add Document</Button>
                  <Button
                    variant={"outline"}
                    onClick={() => setIsOpenDocumentModal(false)}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
      <Modal show={removeDocumentConfirmModal}>
        <div>
          <p className=" roboto text-center font-bold text-2xl text-gray-700">
            Are you sure you want to delete this document?
          </p>
          <div className="mt-4 space-x-3 text-center">
            <Button
              className="font-semibold"
              variant={"destructive"}
              onClick={handleDeleteDocument}
            >
              Yes
            </Button>
            <Button
              className="font-semibold"
              onClick={() => {
                setRemoveDocumentConfirmModal(false);
                setIsOpenDocumentModal(true);
              }}
              variant={"outline"}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
      <GeneratePasswordModal
        open={isGeneratePasswordModalOpen}
        company_employee_id={selectedEmployeeId}
        onClose={() => setIsGeneratePasswordModalOpen(false)}
      />
    </>
  );
}

export default EmployeeDetails;
